import { Info as InfoIcon, Help, Settings as SettingsIcon, PlaylistRemove } from '@mui/icons-material';
import { Badge, IconButton, Popover } from '@mui/material';
import { styled } from '@mui/system';
import Icon from 'Components/_NewElements/Icon';
import ToolTip from 'Components/ToolTip';
import { withCapabilities } from 'Containers/CapabilityHandler';
import { isPlatform } from 'neuro-constants';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { assertCapabilities } from 'Store/index';
import PlatformCapabilities from '../../../config/capabilities';
import { PlatformToArchiveMap } from '../../../config/platformSettings';
import colors from '../../../config/theme/colors';
import { actions } from '../../../store/session';
import { fetchWithOptions } from '../../../utility/fetch';
import { parseJWTFromCookie } from '../../../utility/jwtAuthTools';
import { Container, Item } from '../../Grid';
import logoutIcon from '../images/icon-logout.svg';

import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';
import type { INeuroDocument } from 'neuro-data-structures';
import type { TDispatch } from 'Store/index';

const StyledTable = styled('table')({
  margin: '-0.25rem 0.7rem -0.25rem -0.1rem',
  fontSize: '1.8rem',
});

const StyledMenuItem = styled('div')(({ noTopBorder }: { noTopBorder?: boolean }) => ({
  padding: '0.5rem 1rem',
  backgroundColor: colors.white,
  borderTop: noTopBorder ? 'none' : `1px solid ${colors.gray}`,
  '&:hover': {
    backgroundColor: colors.lightGray,
  },
  cursor: 'pointer',
}));

const NameCircle = styled('div')({
  backgroundColor: colors.white,
  borderRadius: '50rem',
  width: '4.5rem',
  height: '4.5rem',
  color: colors.brandBlue.dark,
  letterSpacing: '0.1rem',
});

const StyledBurgerMenuContainer = styled('div')({
  display: 'flex',
  width: '40px',
  height: '40px',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledBurgerMenuBar = styled('span', {
  shouldForwardProp: prop => prop !== 'open',
})(({ open }: IStyleProps) => ({
  position: 'relative',
  display: 'block',
  opacity: 1,
  width: '30px',
  height: '3px',
  background: colors.tertiary,
  padding: 0,
  marginBottom: '5px',
  transition: 'opacity 100ms, transform 100ms, position 100ms',
  '&:first-of-type': {
    transform: `rotate(${open ? 45 : 0}deg)`,
    top: `${open ? 8 : 0}px`,
  },
  '&:nth-of-type(2)': {
    opacity: `${open ? 0 : 1}`,
  },
  '&:nth-of-type(3)': {
    marginBottom: 0,
    transform: `rotate(${open ? 135 : 0}deg)`,
    top: `${open ? -8 : 0}px`,
  },
}));

const iconStyle = {
  display: 'block',
  margin: '0 0 0 0.2rem',
  padding: '0',
  fontSize: '2.5rem',
  color: colors.tertiary,
};

interface IStyleProps {
  open?: boolean;
}

/** Single item in user dropdown burger menu. */
const BrgrMenuItem = (props: {
  localizationKey: string;
  icon: React.JSX.Element;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  noTopBorder?: boolean;
}): React.JSX.Element => (
  <StyledMenuItem onClick={props.onClick} noTopBorder={props.noTopBorder}>
    <StyledTable>
      <tbody>
        <tr>
          <td style={{ width: '3.5rem' }}>{props.icon}</td>
          <td style={{ color: colors.black }}>
            <FormattedMessage id={props.localizationKey} />
          </td>
        </tr>
      </tbody>
    </StyledTable>
  </StyledMenuItem>
);

const SeparatorItem = () => (
  <div>
    <div
      style={{
        margin: '1rem 0rem 0rem 0rem',
        padding: 0,
        paddingTop: '1rem',
        borderTop: `3px double ${colors.gray}`,
      }}
    />
  </div>
);

const LogoutIcon = styled(({ ...other }) => <img color={'red'} src={logoutIcon} {...other} />)`
  display: block;
  height: 3rem;
  width: 3rem;
`;

interface IMenuContentProps extends ICapabilityContextProps {
  toggleUserMenu: (e: React.MouseEvent<SVGSVGElement | HTMLElement>) => void;
  onClickLogOut: () => void;
  closePatientMessaging: () => void;
  session: ISessionStore;
  setUserSettingsShow: (s: boolean) => () => void;
  setInfoShow: (s: boolean) => () => void;
  orgSettings?: IState['settings']['orgSettings'];
  setDDOpen: (s: boolean) => () => void;
  removedDocuments?: INeuroDocument[];
}
class MenuContent extends React.Component<IMenuContentProps> {
  currentPlatform: string | undefined;
  transferablePlatform: boolean | undefined;
  pid: string | undefined;
  session: ISessionStore | undefined;
  props: IMenuContentProps;

  constructor(props: IMenuContentProps) {
    super(props);
    this.transferablePlatform = isPlatform(this.currentPlatform)
      ? PlatformToArchiveMap[this.currentPlatform].length > 0
      : false;
    this.pid = props.session.data?.patientid;
    this.props = props;
  }

  render() {
    const messagingSettings = this.props.orgSettings?.messagingSettings;
    const messageCenterCapa = messagingSettings && Object.keys(messagingSettings).length > 0;

    const deleteLogCapa = assertCapabilities(
      [PlatformCapabilities.EDITLOG_DELETELOG_VIEW],
      this.props.capabilityGroups ?? {},
    );

    const hasRemovedDocuments = (this.props.removedDocuments ?? []).length > 0;

    return (
      <>
        {this.props.session.data?.patientid && deleteLogCapa && hasRemovedDocuments ? (
          <>
            <BrgrMenuItem
              localizationKey="general.deleteHistory"
              icon={<PlaylistRemove style={iconStyle} />}
              onClick={(e: React.MouseEvent<HTMLElement>): void => {
                this.props.toggleUserMenu(e);
                this.props.setDDOpen(true)();
              }}
              noTopBorder
            />
            <SeparatorItem />
          </>
        ) : null}
        <BrgrMenuItem
          localizationKey="header.userSettings.settings"
          noTopBorder
          icon={
            <SettingsIcon
              style={{
                display: 'block',
                margin: '0 0 0 0.2rem',
                padding: '0',
                fontSize: '2.5rem',
                color: colors.tertiary,
              }}
            />
          }
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            this.props.toggleUserMenu(e);
            this.props.setUserSettingsShow(true)();
          }}
        />
        {messageCenterCapa && (
          <Link to="/messaging" onClick={() => this.props.closePatientMessaging()}>
            <BrgrMenuItem
              localizationKey="header.messagingCenter"
              icon={
                <div style={{ margin: '0 0 0 0.2rem' }}>
                  <Icon icon="mail" color={colors.tertiary} />
                </div>
              }
              onClick={(e: React.MouseEvent<HTMLElement>): void => {
                this.props.toggleUserMenu(e);
              }}
            />
          </Link>
        )}
        <BrgrMenuItem
          localizationKey="header.softwareInstructions"
          icon={<Help style={iconStyle} />}
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            fetchWithOptions(`/api/read/fs/softwareinstruction`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
              .then(
                (res: Response) => {
                  if (res.status === 200) {
                    return res.blob();
                  } else {
                    throw new Error('unexpected status ' + res.status);
                  }
                },
                (error: unknown) => {
                  throw error;
                },
              )
              .then(res => {
                const objectURL = URL.createObjectURL(res);
                window.open(objectURL, '_blank');
              })
              .catch(() => {
                return;
              });
            this.props.toggleUserMenu(e);
          }}
        />

        <BrgrMenuItem
          localizationKey="header.about"
          icon={<InfoIcon style={iconStyle} />}
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            this.props.setInfoShow(true)();
            this.props.toggleUserMenu(e);
          }}
        />
        <BrgrMenuItem
          localizationKey="header.logout"
          icon={<LogoutIcon />}
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            this.props.onClickLogOut();
            this.props.toggleUserMenu(e);
          }}
        />
      </>
    );
  }
}

const MenuContentCapa = withCapabilities(MenuContent);

type TProps = IBurgerUsericon & IDispatchFromProps & IStateFromProps;

class BurgerUsericon extends React.Component<TProps> {
  userInfoMenuRef: React.RefObject<HTMLDivElement | null>;

  constructor(props: TProps) {
    super(props);
    this.userInfoMenuRef = React.createRef();
    this.state = { users: null };
  }

  render(): React.JSX.Element {
    const { session, showMenu, setUserSettingsShow, setInfoShow, toggleUserMenu, concurrentUsers } = this.props;

    const otherUsers = concurrentUsers
      .filter(s => s.useruuid !== session.data?.useruuid)
      .map(user => {
        const users = this.props.session.orgUsers ?? [];
        const vaultUser = users.find(u => u.userId === user.useruuid);

        if (!vaultUser && user.orgid === this.props.session.data?.orgid)
          return { name: 'thisOrgUser', id: user.useruuid };
        if (!vaultUser) return { name: 'otherOrgUser', id: user.useruuid };

        return { name: `${vaultUser.lastNames} ${vaultUser.firstNames}`, id: user.useruuid };
      });

    return (
      <Container style={{ height: '100%', color: colors.tertiary }} alignItems="center" justifyContent="flex-end">
        <Item>
          <IconButton
            id="hamburger"
            color="inherit"
            disableRipple
            style={{
              cursor: 'pointer',
              padding: 0,
              zIndex: showMenu ? 2000 : undefined,
            }}
            onClick={toggleUserMenu}
          >
            <StyledBurgerMenuContainer>
              <div>
                <StyledBurgerMenuBar open={!!showMenu} />
                <StyledBurgerMenuBar open={!!showMenu} />
                <StyledBurgerMenuBar open={!!showMenu} />
              </div>
            </StyledBurgerMenuContainer>
          </IconButton>
          <Popover
            open={!!showMenu}
            anchorEl={showMenu}
            onClose={toggleUserMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ elevation: 3, style: { borderRadius: '0.3rem' } }}
            //MenuListProps={{ style: { padding: 0 } }}
            ref={this.userInfoMenuRef}
          >
            <MenuContentCapa
              toggleUserMenu={toggleUserMenu}
              onClickLogOut={this.props.logOut}
              closePatientMessaging={this.props.closePatientMessaging}
              session={session}
              setUserSettingsShow={setUserSettingsShow}
              setInfoShow={setInfoShow}
              orgSettings={this.props.orgSettings}
              setDDOpen={this.props.setDDOpen}
              removedDocuments={this.props.removedDocuments}
            />
          </Popover>
        </Item>
        <Item style={{ paddingLeft: '2rem' }}>
          <Badge
            badgeContent={
              otherUsers.length > 0 ? (
                <ToolTip
                  content={'+' + otherUsers.length}
                  title={
                    <>
                      <FormattedMessage id="header.userLookingAtSamePatient" />
                      <ul>
                        {otherUsers.map(u => (
                          <li key={u.id}>
                            {u.name === 'thisOrgUser' ? (
                              <FormattedMessage id="header.thisOrgUser" />
                            ) : u.name === 'otherOrgUser' ? (
                              <FormattedMessage id="header.otherOrgUser" />
                            ) : (
                              u.name
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  }
                  hover
                ></ToolTip>
              ) : undefined
            }
            color={'primary'}
            slotProps={{ badge: { style: { fontSize: '1.6rem', fontWeight: 600 } } }}
          >
            <NameCircle>
              <Container alignItems={'center'} justifyContent={'center'} style={{ height: '100%' }}>
                <Item>
                  <h2 style={{ margin: 0, padding: 0 }}>
                    {session.data?.userfirstnames.charAt(0)}
                    {session.data?.userlastnames.charAt(0)}
                  </h2>
                </Item>
              </Container>
            </NameCircle>
          </Badge>
        </Item>
      </Container>
    );
  }
}

interface IDispatchFromProps {
  logOut: () => void;
  closePatientMessaging: () => void;
}

const mapDispatchToProps = (dispatch: any): IDispatchFromProps => ({
  logOut: (): void => dispatch(actions.logOut()),
  closePatientMessaging: () => dispatch(actions.setMessagesSessionSettings({ open: false })),
});

interface IStateFromProps {
  orgSettings?: IState['settings']['orgSettings'];
  concurrentUsers: Array<{ orgid: string; useruuid: string; patientid: string | null; jti: string }>;
  removedDocuments?: IState['documents']['removedDocuments'];
}

const mapStateToProps = (state: IState): IStateFromProps => ({
  orgSettings: state.settings.orgSettings,
  concurrentUsers: state.session.websocket.subscribers,
  removedDocuments: state.documents.removedDocuments,
});

interface IBurgerUsericon {
  session: ISessionStore;
  dispatch: TDispatch;
  toggleUserMenu: (e: React.MouseEvent<SVGSVGElement | HTMLElement>) => void;
  showMenu: SVGSVGElement | HTMLElement | null;
  setUserSettingsShow: (s: boolean) => () => void;
  setInfoShow: (s: boolean) => () => void;
  setDDOpen: (s: boolean) => () => void;
}

export default connect(mapStateToProps, mapDispatchToProps)(BurgerUsericon);
